import { useState, useEffect } from "react";
import styled from "styled-components";
import UserService from "../../services/UserService";
import { Container, Input } from "../CommonComponents/DataCard";

const InputWrapper = styled.div`
  position: relative;
  width: 100%;
`;

const Dropdown = styled.ul`
  margin: 0;
  padding: 0;
  list-style: none;
  border: 1px solid ${({ theme }) => theme.colors.lineColor};
  border-radius: 6px;
  max-height: 200px;
  overflow-y: auto;
  background: ${({ theme }) => theme.colors.card};
  position: absolute;
  top: 100%;
  left: 0;
  right: 0;
  z-index: 1000;
  margin-top: 4px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
`;

const DropdownItem = styled.li`
  padding: 10px 12px;
  cursor: pointer;
  border-bottom: 1px solid ${({ theme }) => theme.colors.lineColor}33;
  color: ${({ theme }) => theme.colors.text};

  &:last-child {
    border-bottom: none;
  }

  &:hover {
    background: ${({ theme }) => theme.colors.primary}22;
  }
`;

const UserInfo = styled.div`
  font-weight: 500;
  color: ${({ theme }) => theme.colors.text};
`;

const Username = styled.div`
  font-size: 12px;
  color: ${({ theme }) => theme.colors.lineColor};
  margin-top: 2px;
`;

const SelectedUser = styled.div`
  background: ${props => props.error ? '#fee' : '#e8f5e9'};
  border: 1px solid ${props => props.error ? '#f44336' : '#4caf50'};
  border-radius: 6px;
  padding: 10px 12px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-top: 12px;
`;

const RemoveButton = styled.button`
  background: transparent;
  border: none;
  color: #666;
  cursor: pointer;
  font-size: 18px;
  padding: 0 4px;

  &:hover {
    color: #f44336;
  }
`;

const ErrorText = styled.p`
  color: #f44336;
  font-size: 14px;
  margin: 8px 0 0 0;
`;

const Label = styled.label`
  display: block;
  margin-bottom: 8px;
  font-weight: 500;
  color: ${({ theme }) => theme.colors.text};
`;

export default function UserSelector({ selectedUser, setSelectedUser, error }) {
    const [query, setQuery] = useState("");
    const [users, setUsers] = useState([]);
    const [loading, setLoading] = useState(false);

    useEffect(() => {
        if (query.length < 2) {
            setUsers([]);
            return;
        }

        const handler = setTimeout(async () => {
            setLoading(true);
            try {
                const results = await UserService.searchUsers(query, 0, 10);
                // Extract content array from Page object
                const userList = results.content || results;
                setUsers(userList);
            } catch (err) {
                console.error("Greška pri pretraživanju korisnika:", err);
                setUsers([]);
            } finally {
                setLoading(false);
            }
        }, 300);

        return () => clearTimeout(handler);
    }, [query]);

    const handleSelect = (user) => {
        setSelectedUser(user);
        setQuery("");
        setUsers([]);
    };

    const handleRemove = () => {
        setSelectedUser(null);
    };

    return (
        <Container>
            <Label>Odabir korisnika:</Label>
            <InputWrapper>
                <Input
                    value={query}
                    placeholder="Pretraži korisnika (ime, prezime ili username)..."
                    onChange={(e) => setQuery(e.target.value)}
                    disabled={selectedUser !== null}
                    style={{
                        marginBottom: "0px",
                        borderColor: error ? '#f44336' : undefined
                    }}
                />
                {loading && (
                    <span style={{
                        position: "absolute",
                        right: "12px",
                        top: "12px",
                        fontSize: "16px"
                    }}>
                        ⏳
                    </span>
                )}
                {users.length > 0 && !selectedUser && (
                    <Dropdown>
                        {users.map((user) => (
                            <DropdownItem
                                key={user.id}
                                onClick={() => handleSelect(user)}
                            >
                                <UserInfo>
                                    {user.name} {user.surname}
                                </UserInfo>
                                <Username>
                                    @{user.username}
                                </Username>
                            </DropdownItem>
                        ))}
                    </Dropdown>
                )}
            </InputWrapper>

            {error && !selectedUser && (
                <ErrorText>Morate odabrati korisnika</ErrorText>
            )}

            {selectedUser && (
                <SelectedUser error={error}>
                    <div>
                        <strong>{selectedUser.name} {selectedUser.surname}</strong>
                        <span style={{ color: "#666", marginLeft: "8px" }}>
                            (@{selectedUser.username})
                        </span>
                    </div>
                    <RemoveButton onClick={handleRemove}>✖</RemoveButton>
                </SelectedUser>
            )}
        </Container>
    );
}
